/**
 * `EscalateDialog` — small modal that confirms (and submits) a
 * "create one GitHub issue for this failure" call to
 * `POST /internal/admin/qa/escalate`. Used from:
 *
 *   - The System Health board (failing checks) — kind=`'health_check'`
 *   - The Test Suite view (failing CI runs)    — kind=`'ci_run'`
 *
 * Owns its own submit state because both call sites want the same
 * three-branch lifecycle (idle → submitting → terminal), AND the
 * terminal state has three flavors (success / not-configured / rate-
 * limited) the dialog renders distinctly. Wrapping it in a higher-up
 * state machine would just relay these branches through props for no
 * benefit.
 *
 * Honest copy throughout:
 *   - On 409 we say "GitHub issues aren't configured yet" (not "error")
 *     and offer to close — the failure is operational, not user-induced.
 *   - On 429 we say "Rate limit reached (10/hour) — try later."
 *   - On success we surface the issue number + html_url with a "View on
 *     GitHub" link so the staff can immediately confirm it landed.
 */

import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';

import type {
  AdminEscalateBody,
  AdminEscalateKind,
  AdminEscalateResponse,
} from '@sill/types';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/**
 * Read-only context the dialog displays + sends. The caller derives
 * this from the on-screen failing item (health check OR CI run).
 *
 * `summary` is the base summary; the staff member's optional note is
 * appended at submit time so the wire payload carries both pieces in
 * one field (the route accepts a single `summary` string).
 */
export interface EscalateContext {
  kind: AdminEscalateKind;
  ref: string;
  /** Base human-readable label of the thing being escalated — typically
   *  the check label or the workflow name. The staff's note is appended
   *  to this when submitting. */
  summary: string;
  status?: string;
  detail?: string;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; response: AdminEscalateResponse }
  | { kind: 'not_configured' }
  | { kind: 'rate_limited' }
  | { kind: 'error'; message: string };

export interface EscalateDialogProps {
  open: boolean;
  context: EscalateContext;
  onClose: () => void;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.kind;
  }
  return 'Unexpected error';
}

export function EscalateDialog({
  open,
  context,
  onClose,
}: EscalateDialogProps): JSX.Element | null {
  const C = useC();
  const api = useAdminApi();
  const [note, setNote] = useState('');
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });

  // Reset internal state every time the dialog opens or the
  // underlying context changes — otherwise re-opening would show a
  // stale "Issue #N created" from a previous escalation.
  useEffect(() => {
    if (open) {
      setNote('');
      setState({ kind: 'idle' });
    }
  }, [open, context.kind, context.ref]);

  const handleNoteChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>): void => {
      // Soft cap at 400 chars so the combined `summary` (label + note)
      // stays inside the server's 500-char cap with headroom for the
      // " — " separator + the label itself.
      const v = e.target.value;
      setNote(v.length > 400 ? v.slice(0, 400) : v);
    },
    [],
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    setState({ kind: 'submitting' });
    const trimmedNote = note.trim();
    const summary =
      trimmedNote.length > 0
        ? `${context.summary} — ${trimmedNote}`
        : context.summary;
    const body: AdminEscalateBody = {
      kind: context.kind,
      ref: context.ref,
      summary,
      ...(context.status !== undefined ? { status: context.status } : {}),
      ...(context.detail !== undefined ? { detail: context.detail } : {}),
    };
    try {
      const response = await api.escalateIssue(body);
      setState({ kind: 'success', response });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === 'conflict') {
          setState({ kind: 'not_configured' });
          return;
        }
        if (err.kind === 'rate_limited') {
          setState({ kind: 'rate_limited' });
          return;
        }
        if (err.kind === 'unauthorized') {
          // The http-client's on_unauthorized hook already routed to
          // the login view; nothing to render here.
          return;
        }
      }
      setState({ kind: 'error', message: errorMessage(err) });
    }
  }, [api, context.detail, context.kind, context.ref, context.status, context.summary, note]);

  if (!open) return null;

  const submitting = state.kind === 'submitting';
  const terminal =
    state.kind === 'success' ||
    state.kind === 'not_configured' ||
    state.kind === 'rate_limited';

  const kindLabel = context.kind === 'health_check' ? 'Health check' : 'CI run';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Escalate to GitHub issue"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          width: '100%',
          maxWidth: 520,
          padding: 20,
          // Don't stretch past the viewport on mobile — let the inner
          // content scroll. The outer overflow handles the rest.
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Escalate
        </div>
        <h2
          style={{
            fontFamily: sansFont,
            fontSize: 18,
            fontWeight: 500,
            color: C.text,
            marginBottom: 12,
            lineHeight: 1.3,
          }}
        >
          Create a GitHub issue for this failure
        </h2>

        {/* Read-only context block — what the wire payload will carry. */}
        <div
          style={{
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            padding: 12,
            marginBottom: terminal ? 12 : 12,
          }}
        >
          <ContextRow label="Kind" value={`${kindLabel} (${context.kind})`} mono />
          <ContextRow label="Ref" value={context.ref} mono />
          {context.status !== undefined ? (
            <ContextRow label="Status" value={context.status} mono />
          ) : null}
          <ContextRow label="Summary" value={context.summary} />
          {context.detail !== undefined && context.detail.length > 0 ? (
            <ContextRow label="Detail" value={context.detail} mono />
          ) : null}
        </div>

        {/* Plain-language explanation of what submitting actually does.
            Calm, secondary styling — informational, not a warning. Only
            shown before a terminal state (after success/rate-limited/
            not-configured the banners carry their own copy). */}
        {!terminal ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.55,
              marginBottom: 16,
              wordBreak: 'break-word',
            }}
          >
            Submitting opens a private GitHub issue in{' '}
            <span style={{ fontFamily: monoFont, fontSize: 11 }}>
              siteforbiz/sill
            </span>{' '}
            with this context and your note, labeled{' '}
            <span style={{ fontFamily: monoFont, fontSize: 11 }}>
              qa-escalation
            </span>
            . Whoever watches the repo's issues is notified — there's no
            automatic assignee. It's a tracked item, not an alert; keep
            secrets out of the note.
          </div>
        ) : null}

        {/* Note field — only meaningful before submit. */}
        {!terminal ? (
          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor="escalate-note"
              style={{
                display: 'block',
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Note (optional)
            </label>
            <textarea
              id="escalate-note"
              value={note}
              onChange={handleNoteChange}
              disabled={submitting}
              placeholder="Anything to add for the on-call engineer? (optional)"
              rows={3}
              style={{
                width: '100%',
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                padding: '8px 10px',
                lineHeight: 1.4,
                resize: 'vertical',
                minHeight: 70,
              }}
            />
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.dim,
                marginTop: 4,
                letterSpacing: 0.5,
              }}
            >
              {note.length} / 400
            </div>
          </div>
        ) : null}

        {/* Terminal banners */}
        {state.kind === 'success' ? <SuccessBanner response={state.response} /> : null}
        {state.kind === 'not_configured' ? <NotConfiguredBanner /> : null}
        {state.kind === 'rate_limited' ? <RateLimitedBanner /> : null}
        {state.kind === 'error' ? <ErrorBanner message={state.message} /> : null}

        {/* Action row */}
        <div className="flex items-center justify-end gap-2 flex-wrap" style={{ marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              padding: '8px 14px',
              borderRadius: 2,
              minHeight: 40,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {terminal ? 'Close' : 'Cancel'}
          </button>
          {!terminal ? (
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                color: C.ink,
                background: submitting ? C.surface2 : C.lime,
                border: `1px solid ${submitting ? C.border : C.lime}`,
                padding: '8px 14px',
                borderRadius: 2,
                minHeight: 40,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Creating issue…' : 'Create GitHub issue'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ContextRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3" style={{ marginBottom: 6 }}>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.dim,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          minWidth: 60,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: mono ? monoFont : sansFont,
          fontSize: mono ? 12 : 13,
          color: C.body,
          wordBreak: 'break-word',
          lineHeight: 1.4,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SuccessBanner({ response }: { response: AdminEscalateResponse }): JSX.Element {
  const C = useC();
  const num = response.issue_number;
  const url = response.html_url;
  return (
    <div
      role="status"
      style={{
        background: C.mintDim,
        border: '1px solid rgba(90,255,205,0.3)',
        borderRadius: 2,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.mintFg,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        Issue created
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          lineHeight: 1.5,
        }}
      >
        {num !== undefined ? (
          <>
            Issue{' '}
            <span style={{ fontFamily: monoFont, color: C.text }}>#{num}</span> was
            opened in <span style={{ fontFamily: monoFont }}>siteforbiz/sill</span>.
          </>
        ) : (
          <>The issue was created.</>
        )}
        {url !== undefined ? (
          <>
            {' '}
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1"
              style={{ color: C.text, textDecoration: 'underline' }}
            >
              View on GitHub
              <ExternalLink size={12} aria-hidden="true" />
            </a>
          </>
        ) : null}
      </div>
    </div>
  );
}

function NotConfiguredBanner(): JSX.Element {
  const C = useC();
  return (
    <div
      role="status"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        GitHub issues not configured
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          lineHeight: 1.5,
        }}
      >
        GitHub issues aren't configured yet — set the issues token to
        enable escalation. The check is still visible on the board.
      </div>
    </div>
  );
}

function RateLimitedBanner(): JSX.Element {
  const C = useC();
  return (
    <div
      role="status"
      style={{
        background: C.amberDim,
        border: '1px solid rgba(255,182,39,0.3)',
        borderRadius: 2,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.amberFg,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        Rate limited
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          lineHeight: 1.5,
        }}
      >
        Rate limit reached (10/hour) — try later. The window resets on
        a rolling basis.
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }): JSX.Element {
  const C = useC();
  return (
    <div
      role="alert"
      style={{
        background: C.coralDim,
        border: '1px solid rgba(255,107,71,0.3)',
        borderRadius: 2,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.coralFg,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        Could not create issue
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 12,
          color: C.coralFg,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {message}
      </div>
    </div>
  );
}
